import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Organization } from '@newgenus/common';
import { MaterialModule } from '../../../material.module';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';
import { OrgDialogService } from '../../services/org-dialog.service';
import { ProfileColorService } from '../../services/profile-color.service';
import { CatalogPermissionRef, CatalogRef, CatalogRefs } from '../../shared/feature-permission-catalog';

export type FeaturePermissionMode = 'features' | 'permissions';

/** One feature or permission, as the Features / Permissions lists hold it. */
export interface FeaturePermissionDetailsItem {
    title: string;
    /** Permissions: the feature the permission belongs to. */
    featureName?: string;
    description: string;
    /** Features: the permissions the feature offers. */
    permissions: CatalogPermissionRef[];
    granted: boolean;
    refs: CatalogRefs;
}

export interface FeaturePermissionDetailsData {
    organization: Organization;
    mode: FeaturePermissionMode;
    item: FeaturePermissionDetailsItem;
}

/**
 * Details of a feature or a permission (Paul, 28 Oct: "like the others"), opened from the
 * Features and Permissions lists. Laid out like the hat and security group details: counts down
 * the side, then the security groups that grant it, the hats that carry it and the staff who
 * have it. Read-only; a group or hat opens its own details.
 */
@Component({
    selector: 'shared-org-feature-permission-details-dialog',
    imports: [MaterialModule, CommonModule],
    templateUrl: './org-feature-permission-details-dialog.html',
    styleUrls: ['./org-feature-permission-details-dialog.scss'],
})
export class OrgFeaturePermissionDetailsDialogComponent implements OnInit {
    public entityColors = inject(OrgEntityColorsService);
    private dialogService = inject(OrgDialogService);
    private profileColors = inject(ProfileColorService);

    public sections = { permissions: true, securityGroups: true, hats: true, staff: true };

    constructor(@Inject(MAT_DIALOG_DATA) public data: FeaturePermissionDetailsData) {}

    public get item(): FeaturePermissionDetailsItem {
        return this.data.item;
    }

    public get isFeature(): boolean {
        return this.data.mode === 'features';
    }

    public get icon(): string {
        return this.isFeature ? 'extension' : 'vpn_key';
    }

    public get color(): string {
        return this.entityColors.getColor(this.data.mode);
    }

    public ngOnInit(): void {
        this.profileColors.ensureLoadedMany(this.item.refs.staff.map((s) => s.key));
    }

    public count(n: number, one: string, many: string): string {
        return `${n} ${n === 1 ? one : many}`;
    }

    public openSecurityGroup(ref: CatalogRef): void {
        const groups = (this.data.organization?.orgSecurityGroups || {}) as Record<string, unknown>;
        this.dialogService.openSecurityGroupDetailsDialog(ref.key, groups[ref.key]);
    }

    public openHat(ref: CatalogRef): void {
        const hats = (this.data.organization?.orgHats || {}) as Record<string, unknown>;
        this.dialogService.openHatDetailsDialog(ref.key, hats[ref.key]);
    }

    public avatarUrl(ref: CatalogRef): string {
        const user = (this.data.organization?.orgUsers as Record<string, { photoThumbnail?: string }> | undefined)?.[
            ref.key
        ];
        return user?.photoThumbnail || this.profileColors.getAvatarUrl(ref.key, ref.name || 'User', 40);
    }

    public trackByKey(_: number, ref: { key: string }): string {
        return ref.key;
    }
}
